import { Channels } from './constants';
import { local, session } from './storage';

export interface IValue<V extends string | number | undefined> {
  Value: V;
}

export interface RequiredValueOptions {
  name: string;
}

export interface OptionalOptions<V> {
  persist: 'local' | 'session' | 'never';
  broadcast: boolean;
  initialValue: V | (() => V);
  onSet: (currentValue: V) => V | undefined;
}

export type ValueOptions<V> = RequiredValueOptions & Partial<OptionalOptions<V>>;

const defaultOptions: OptionalOptions<undefined> = {
  persist: 'never',
  broadcast: false,
  initialValue: undefined,
  onSet: undefined,
};

/**
 * Helper class that can be configured to persist a primitive value.
 *
 * It can persist the value locally, within the session, or across tabs/windows.
 */
export class Value<V extends string | number | undefined> implements IValue<V> {
  private name: string;
  private value: V;
  private options: ValueOptions<V>;
  private channel?: BroadcastChannel;

  constructor(options: ValueOptions<V>) {
    this.name = options.name;
    this.options = {
      ...defaultOptions,
      ...options,
    };

    const persistedValue = this.getPersistedValue();
    if (persistedValue) {
      this.setValue(persistedValue, 'persist')
    } else if (typeof options.initialValue === 'function') {
      this.setValue(options.initialValue());
    } else {
      this.setValue(options.initialValue);
    }

    if (this.options.broadcast) {
      this.setupBroadcastChannel();
    }
  }

  public get Value() {
    return this.value;
  }

  public set Value(value: V) {
    this.setValue(value);
  }

  private setValue(value: V, method?: string) {
    if (typeof this.options.onSet === 'function') {
      this.value = this.options.onSet(value);
    } else {
      this.value = value;
    }
    if (method !== 'broadcast') {
      this.broadcast(value);
    }
    if (method !== 'persist') {
      this.setPersistedValue(value);
    }
  }

  private broadcast(value: V) {
    if (!this.options.broadcast || !this.channel) {
      return;
    }
    this.channel.postMessage({
      type: `response-${this.name}`,
      value: value,
    });
  }

  private getPersistedValue(): V | undefined {
    if (this.options.persist === 'local') {
      return local.read<V>(this.name);
    } else if (this.options.persist === 'session') {
      return session.read<V>(this.name);
    }

    return;
  }

  private setPersistedValue(value: V) {
    if (this.options.persist === 'local') {
      return local.write(this.name, value);
    } else if (this.options.persist === 'session') {
      return session.write(this.name, value);
    }
  }

  private setupBroadcastChannel() {
    if (
      typeof window === 'undefined' ||
      typeof window.BroadcastChannel === 'undefined'
    ) {
      return;
    }

    const ReqMessage = `request-${this.name}`;
    const ResMessage = `response-${this.name}`;

    this.channel = new BroadcastChannel(Channels.Broadcast);

    this.channel.addEventListener('message', event => {
      switch (event.data?.type) {
        case ReqMessage: {
          this.channel.postMessage({
            type: ResMessage,
            value: this.value,
          });
          break;
        }
        case ResMessage: {
          const { value } = event.data;
          this.setValue(value, 'broadcast');
          break;
        }
      }
    });
    this.channel.postMessage({ type: ReqMessage });
  }
}
